import { Component, Prop, h, Element, State, Build } from '@stencil/core';
import { getConstant } from '../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../utils/helpers/hydration';

const IMAGE_BASE_TRUST = 'https://marketing-static.signnow.com/349/images/trust-inner/';
const IMAGE_BASE_APPS = 'https://marketing-static.signnow.com/349/images/solutions-catalog/';

/**
 * Transformation section component
 * Displays key metrics and facts about SignNow with reveal animations
 *
 * @product signnow
 * @version 1.0.0
 * @category SignNow
 * @contentConfig Transformation section with metrics cards
 * @example <sn-transformation-type-1 constants='{"TRANSFORMATION_SUBTITLE":"We spread the word about digital transformation","TRANSFORMATION_MESSAGE":"signNow empowers users across every industry to embrace seamless and error-free eSignature workflows for better business outcomes.","TRANSFORMATION_CARD_1_DESC":"completion rate of sent documents","TRANSFORMATION_CARD_1_PROGRESS":"80% completed","TRANSFORMATION_CARD_2_DESC":"average for a sent to signed document","TRANSFORMATION_CARD_3_DESC":"out-of-the-box integrations","TRANSFORMATION_CARD_4_DESC":"average number of signature invites sent in a week","TRANSFORMATION_CARD_5_DESC":"users in Education industry","TRANSFORMATION_CARD_6_DESC":"clicks minimum to sign a document","TRANSFORMATION_CARD_7_DESC":"API calls a week","LINK__TRANSFORMATION_APPS_GDRIVE":"/integrations/googledrive","LINK__TRANSFORMATION_APPS_TEAMS":"/integrations/teams","LINK__TRANSFORMATION_APPS_WORD":"/integrations/word","LINK__TRANSFORMATION_APPS_SALESFORCE":"/integrations/salesforce","LINK__TRANSFORMATION_APPS_NETSUITE":"/integrations/netsuite"}'></sn-transformation-type-1>
 * @constant TRANSFORMATION_SUBTITLE - "We spread the word about digital transformation"
 * @constant TRANSFORMATION_MESSAGE - "signNow empowers users across every industry to embrace seamless and error-free eSignature workflows for better business outcomes."
 * @constant TRANSFORMATION_CARD_1_DESC - "completion rate of sent documents"
 * @constant TRANSFORMATION_CARD_1_PROGRESS - "80% completed"
 * @constant TRANSFORMATION_CARD_2_DESC - "average for a sent to signed document"
 * @constant TRANSFORMATION_CARD_3_DESC - "out-of-the-box integrations"
 * @constant TRANSFORMATION_CARD_4_DESC - "average number of signature invites sent in a week"
 * @constant TRANSFORMATION_CARD_5_DESC - "users in Education industry"
 * @constant TRANSFORMATION_CARD_6_DESC - "clicks minimum to sign a document"
 * @constant TRANSFORMATION_CARD_7_DESC - "API calls a week"
 * @constant LINK__TRANSFORMATION_APPS_GDRIVE - "/integrations/googledrive" - Link for the Google Drive integration badge
 * @constant LINK__TRANSFORMATION_APPS_TEAMS - "/integrations/teams" - Link for the Microsoft Teams integration badge
 * @constant LINK__TRANSFORMATION_APPS_WORD - "/integrations/word" - Link for the Microsoft Word integration badge
 * @constant LINK__TRANSFORMATION_APPS_SALESFORCE - "/integrations/salesforce" - Link for the Salesforce integration badge
 * @constant LINK__TRANSFORMATION_APPS_NETSUITE - "/integrations/netsuite" - Link for the NetSuite integration badge
 */
@Component({
  tag: 'sn-transformation-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnTransformationType1 {
  @Element() el!: HTMLElement;

  /**
   * Component constants object containing all required text constants
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  /**
   * HTML tag for the heading element (h1, h2, h3, h4, h5, h6)
   * @default "h2"
   */
  @Prop() headingTag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h2';

  @State() isVisible: boolean = false;

  private observer: IntersectionObserver;

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
  }

  componentDidLoad() {
    if (!Build.isBrowser) return;
    this.initObserver();
  }

  disconnectedCallback() {
    if (!Build.isBrowser) return;
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private initObserver() {
    if (!Build.isBrowser) return;

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              this.observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1 },
      );

      const animatedElements = this.el.querySelectorAll('[data-animate]');
      animatedElements.forEach(el => this.observer.observe(el));
    } else {
      // Fallback for browsers without IntersectionObserver
      const animatedElements = this.el.querySelectorAll('[data-animate]');
      animatedElements.forEach(el => el.classList.add('is-visible'));
    }
  }

  render() {
    return (
      <me-section class="transformation-type-1" yGap="2xl" yGapLg="5xl">
        <me-row direction="column" directionLg="column">
          <me-column spanLg="12" class="transformation-type-1__heading-column">
            <me-heading variant="2" class="transformation-type-1__heading" tag={this.headingTag}>
              {getConstant(this.constants, 'TRANSFORMATION_SUBTITLE', 'We spread the word about digital transformation')}
            </me-heading>
            <me-text class="transformation-type-1__message">
              {getConstant(
                this.constants,
                'TRANSFORMATION_MESSAGE',
                'signNow empowers users across every industry to embrace seamless and error-free eSignature workflows for better business outcomes.',
              )}
            </me-text>
          </me-column>

          <me-column spanLg="12" class="transformation-type-1__cards">
            {/* Card 1: Completion Rate (80%) - spans 2 columns */}
            <div class="transformation-type-1__card transformation-type-1__card--completion-rate">
              <div class="transformation-type-1__card-wrapper">
                <div class="transformation-type-1__card-content">
                  <me-heading variant="3" class="transformation-type-1__card-title">
                    80%
                  </me-heading>
                  <me-text class="transformation-type-1__card-description">
                    {getConstant(this.constants, 'TRANSFORMATION_CARD_1_DESC', 'completion rate of sent documents')}
                  </me-text>
                </div>

                <div class="completion-rate-illustration">
                  {/* Skip SSR for animated elements - improves SSR performance */}
                  {Build.isBrowser && (
                    <div class="progress" data-animate="fade-in">
                      <div class="progress__title">{getConstant(this.constants, 'TRANSFORMATION_CARD_1_PROGRESS', '80% completed')}</div>
                      <div class="progress__bar"></div>
                    </div>
                  )}

                  {Build.isBrowser && (
                    <div class="document" data-animate="fade-in-up" style={{ transitionDelay: '0.1s' }}>
                      <me-picture
                        srcSet={`${IMAGE_BASE_TRUST}document.png 1x, ${IMAGE_BASE_TRUST}document@2x.png 2x`}
                        webpSrcSet={`${IMAGE_BASE_TRUST}document.webp 1x, ${IMAGE_BASE_TRUST}document@2x.webp 2x`}
                        aspectRatio="auto"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Card 2: Average Sent (1h) */}
            <div class="transformation-type-1__card transformation-type-1__card--average-sent">
              <div class="transformation-type-1__card-wrapper">
                <me-heading variant="3" class="transformation-type-1__card-title">
                  1h
                </me-heading>
                <me-text class="transformation-type-1__card-description">
                  {getConstant(this.constants, 'TRANSFORMATION_CARD_2_DESC', 'average for a sent to signed document')}
                </me-text>
              </div>
            </div>

            {/* Card 3: Integrations (20+) - spans 2 rows */}
            <div class="transformation-type-1__card transformation-type-1__card--integrations-box">
              <div class="transformation-type-1__card-wrapper">
                <me-heading variant="3" class="transformation-type-1__card-title">
                  20+
                </me-heading>
                <me-text class="transformation-type-1__card-description">{getConstant(this.constants, 'TRANSFORMATION_CARD_3_DESC', 'out-of-the-box integrations')}</me-text>

                <div class="apps">
                  {Build.isBrowser && (
                    <div class="apps__item" data-animate="fade-in-left" style={{ transitionDelay: '0.1s' }}>
                      <div class="apps__wrapper">
                        <div class="apps__logo">
                          <me-picture
                            class="apps__icon apps__icon--gdrive"
                            srcSet={`${IMAGE_BASE_APPS}g-drive.png 1x, ${IMAGE_BASE_APPS}g-drive@2x.png 2x`}
                            webpSrcSet={`${IMAGE_BASE_APPS}g-drive.webp 1x, ${IMAGE_BASE_APPS}g-drive@2x.webp 2x`}
                            aspectRatio="auto"
                            alt="Google Drive"
                          />
                        </div>
                        <a
                          class="apps__link"
                          href={getConstant(this.constants, 'LINK__TRANSFORMATION_APPS_GDRIVE', '/integrations/googledrive')}
                          aria-label="signNow for Google Drive"
                        ></a>
                      </div>
                    </div>
                  )}

                  {Build.isBrowser && (
                    <div class="apps__item" data-animate="fade-in-down" style={{ transitionDelay: '0.15s' }}>
                      <div class="apps__wrapper">
                        <div class="apps__logo">
                          <me-picture
                            class="apps__icon apps__icon--ms-teams"
                            srcSet={`${IMAGE_BASE_APPS}ms-teams.png 1x, ${IMAGE_BASE_APPS}ms-teams@2x.png 2x`}
                            webpSrcSet={`${IMAGE_BASE_APPS}ms-teams.webp 1x, ${IMAGE_BASE_APPS}ms-teams@2x.webp 2x`}
                            aspectRatio="auto"
                            alt="Microsoft Teams"
                          />
                        </div>
                        <a
                          class="apps__link"
                          href={getConstant(this.constants, 'LINK__TRANSFORMATION_APPS_TEAMS', '/integrations/teams')}
                          aria-label="signNow for Microsoft Teams"
                        ></a>
                      </div>
                    </div>
                  )}

                  {Build.isBrowser && (
                    <div class="apps__item" data-animate="fade-in-right" style={{ transitionDelay: '0.2s' }}>
                      <div class="apps__wrapper">
                        <div class="apps__logo">
                          <me-picture
                            class="apps__icon apps__icon--ms-word"
                            srcSet={`${IMAGE_BASE_APPS}ms-word.png 1x, ${IMAGE_BASE_APPS}ms-word@2x.png 2x`}
                            webpSrcSet={`${IMAGE_BASE_APPS}ms-word.webp 1x, ${IMAGE_BASE_APPS}ms-word@2x.webp 2x`}
                            aspectRatio="auto"
                            alt="Microsoft Word"
                          />
                        </div>
                        <a class="apps__link" href={getConstant(this.constants, 'LINK__TRANSFORMATION_APPS_WORD', '/integrations/word')} aria-label="signNow for Word"></a>
                      </div>
                    </div>
                  )}

                  {Build.isBrowser && (
                    <div class="apps__item" data-animate="fade-in-up" style={{ transitionDelay: '0.25s' }}>
                      <div class="apps__wrapper">
                        <div class="apps__logo">
                          <me-picture class="apps__icon apps__icon--salesforce" src={`${IMAGE_BASE_APPS}salesforce.svg`} alt="Salesforce" aspectRatio="auto" />
                        </div>
                        <a
                          class="apps__link"
                          href={getConstant(this.constants, 'LINK__TRANSFORMATION_APPS_SALESFORCE', '/integrations/salesforce')}
                          aria-label="signNow for Salesforce"
                        ></a>
                      </div>
                    </div>
                  )}

                  {Build.isBrowser && (
                    <div class="apps__item apps__item--double-size" data-animate="fade-in-right" style={{ transitionDelay: '0.3s' }}>
                      <div class="apps__wrapper">
                        <div class="apps__logo">
                          <me-picture
                            class="apps__icon apps__icon--netsuite"
                            srcSet={`${IMAGE_BASE_APPS}netsuite.png 1x, ${IMAGE_BASE_APPS}netsuite@2x.png 2x`}
                            webpSrcSet={`${IMAGE_BASE_APPS}netsuite.webp 1x, ${IMAGE_BASE_APPS}netsuite@2x.webp 2x`}
                            aspectRatio="auto"
                            alt="NetSuite"
                          />
                        </div>
                        <a
                          class="apps__link"
                          href={getConstant(this.constants, 'LINK__TRANSFORMATION_APPS_NETSUITE', '/integrations/netsuite')}
                          aria-label="signNow for Netsuite"
                        ></a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Card 4: Average Invites (96k) - spans 2 rows */}
            <div class="transformation-type-1__card transformation-type-1__card--average-invites">
              <div class="transformation-type-1__card-wrapper">
                <me-heading variant="3" class="transformation-type-1__card-title">
                  96k
                </me-heading>
                <me-text class="transformation-type-1__card-description">
                  {getConstant(this.constants, 'TRANSFORMATION_CARD_4_DESC', 'average number of signature invites sent in a week')}
                </me-text>

                {Build.isBrowser && (
                  <div class="average-invites-illustration" data-animate="fade-in-up" style={{ transitionDelay: '0.1s' }}>
                    <me-picture src={`${IMAGE_BASE_TRUST}notifications.svg`} alt="" aspectRatio="3 / 4" />
                  </div>
                )}
              </div>
            </div>

            {/* Card 5: Education (28,9k) */}
            <div class="transformation-type-1__card transformation-type-1__card--hippa">
              <div class="transformation-type-1__card-wrapper">
                <div class="transformation-type-1__card-content">
                  <me-heading variant="3" class="transformation-type-1__card-title transformation-type-1__card-title--black">
                    28,9k
                  </me-heading>
                  <me-text class="transformation-type-1__card-description transformation-type-1__card-description--black">
                    {getConstant(this.constants, 'TRANSFORMATION_CARD_5_DESC', 'users in Education industry')}
                  </me-text>
                </div>

                <div class="hippa-illustration">
                  {Build.isBrowser && (
                    <div class="hippa-illustration__item hippa-illustration__item--01" data-animate="fade-in-right" style={{ transitionDelay: '0.2s' }}>
                      <me-picture src={`${IMAGE_BASE_TRUST}Security-Badges-01.svg`} alt="" aspectRatio="auto" />
                    </div>
                  )}
                  {Build.isBrowser && (
                    <div class="hippa-illustration__item hippa-illustration__item--02" data-animate="fade-in-right" style={{ transitionDelay: '0.3s' }}>
                      <me-picture src={`${IMAGE_BASE_TRUST}Security-Badges-02.svg`} alt="" aspectRatio="auto" />
                    </div>
                  )}
                  {Build.isBrowser && (
                    <div class="hippa-illustration__item hippa-illustration__item--03" data-animate="fade-in-right" style={{ transitionDelay: '0.4s' }}>
                      <me-picture src={`${IMAGE_BASE_TRUST}Security-Badges-03.svg`} alt="" aspectRatio="auto" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Card 6: Clicks (2) */}
            <div class="transformation-type-1__card transformation-type-1__card--average-clicks">
              <div class="transformation-type-1__card-wrapper">
                <div class="transformation-type-1__card-content">
                  <me-heading variant="3" class="transformation-type-1__card-title">
                    2
                  </me-heading>
                  <me-text class="transformation-type-1__card-description">
                    {getConstant(this.constants, 'TRANSFORMATION_CARD_6_DESC', 'clicks minimum to sign a document')}
                  </me-text>
                </div>

                {Build.isBrowser && (
                  <div class="clicks-illustration" data-animate="fade-in-up" style={{ transitionDelay: '0.1s' }}>
                    <me-picture src={`${IMAGE_BASE_TRUST}cursor-click-select.svg`} alt="" aspectRatio="auto" />
                  </div>
                )}
              </div>
            </div>

            {/* Card 7: API Calls (14.3M) - spans 2 columns */}
            <div class="transformation-type-1__card transformation-type-1__card--api-cals">
              <div class="transformation-type-1__card-wrapper">
                <div class="transformation-type-1__card-content">
                  <me-heading variant="3" class="transformation-type-1__card-title">
                    14.3M
                  </me-heading>
                  <me-text class="transformation-type-1__card-description">{getConstant(this.constants, 'TRANSFORMATION_CARD_7_DESC', 'API calls a week')}</me-text>
                </div>

                <div class="api-cals-illustration">
                  {Build.isBrowser && (
                    <div class="api-cals-illustration__item api-cals-illustration__item--avatar" data-animate="fade-in-left" style={{ transitionDelay: '0.1s' }}>
                      <me-picture
                        src={`${IMAGE_BASE_TRUST}avatar.png`}
                        srcSet={`${IMAGE_BASE_TRUST}avatar.png 1x, ${IMAGE_BASE_TRUST}avatar@2x.png 2x`}
                        webpSrcSet={`${IMAGE_BASE_TRUST}avatar.webp 1x, ${IMAGE_BASE_TRUST}avatar@2x.webp 2x`}
                        alt="Avatar"
                        aspectRatio="auto"
                      />
                    </div>
                  )}
                  {Build.isBrowser && (
                    <div class="api-cals-illustration__item api-cals-illustration__item--code" data-animate="fade-in-up" style={{ transitionDelay: '0.2s' }}>
                      <me-picture
                        src={`${IMAGE_BASE_TRUST}code.png`}
                        srcSet={`${IMAGE_BASE_TRUST}code.png 1x, ${IMAGE_BASE_TRUST}code@2x.png 2x`}
                        webpSrcSet={`${IMAGE_BASE_TRUST}code.webp 1x, ${IMAGE_BASE_TRUST}code@2x.webp 2x`}
                        alt="Code"
                        aspectRatio="auto"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </me-column>
        </me-row>
      </me-section>
    );
  }
}
